import {
  daysToMonths,
  formatQueryRes,
  getDateStrFromUnixTimestamp,
  IsFullAccess,
  useAppDispatch,
  useAppSelector,
  useGetHistoricQuotaQuery,
  useGetInstanceInfoQuery,
  useGetRegionsInstanceListQuery,
  useGetVmLiveStatusQuery,
  useLazyGetHaInstanceAccessUrlQuery
} from '@monorepo/shared';
import {
  selectAccountWithPermission,
  selectGlobalData,
  selectSystem,
  updateWarnings
} from '@monorepo/shared/store/accountSlice';
import {
  selectDebugKey,
  selectRegions
} from '@monorepo/shared/store/authSlice';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  ButtonToolbar,
  Popover,
  Progress,
  Stack,
  useToaster,
  Whisper
} from 'rsuite';
import InstanceIcon from '../../images/account/instance.svg';
import { MdArrowForward } from 'react-icons/md';
import { MdInfoOutline } from 'react-icons/md';
import { VscTerminalCmd } from 'react-icons/vsc';
import { HomeElms } from '../../config/const';
import { InstanceHistoric } from './InstanceHistoric';
import { ErrorToaster } from './Warning';
import { t } from 'i18next';
import { redirectVmUrl } from '@monorepo/shared/components/InstanceActions';
import { TicketContent } from './AccountDetail';

export const Instance = () => {
  const { state } = useLocation();
  const instid = state?.instid;
  const toaster = useToaster();
  const dispatch = useAppDispatch();
  const debugKey = useAppSelector(selectDebugKey);
  const regions = useAppSelector(selectRegions);
  const globalData = useAppSelector(selectGlobalData);
  const systemData = useAppSelector(selectSystem);
  const account = useAppSelector(selectAccountWithPermission);

  const [queryRegions, setQueryRegions] = useState<Record<string, any>[]>([]);
  const [instance, setInstance] = useState<Record<string, any>>({});
  const [region, setRegion] = useState<Record<string, any>>({});
  const [analyticsStr, setAnalyticsStr] = useState<string>('');
  const [archiveStr, setArchiveStr] = useState<string>('');
  const [dailyData, setDailyData] = useState<Record<string, any>>({
    percent: 0,
    str: '',
    detail: ''
  });
  const [hasTicket, setHasTicket] = useState<boolean>(false);
  const [tickets, setTickets] = useState<Record<string, any>[]>([]);
  const [hasInstance, setHasInstance] = useState<boolean>(false);
  const [disableEnter, setDisableEnter] = useState<boolean>(false);
  const [disableEnterReason, setDisableEnterReason] = useState<string>('');
  const [disableWebssh, setDisableWebssh] = useState<boolean>(false);
  const [disableWebsshReason, setDisableWebsshReason] = useState<string>('');
  const [isAccountFullAccess, setIsAccountFullAccess] =
    useState<boolean>(false);
  const queryParams = {
    ...account,
    instid: instid,
    regions: queryRegions,
    account_region: account.region
  };

  const {
    data: instList,
    isLoading,
    isFetching
  } = useGetRegionsInstanceListQuery(
    {
      ...queryParams
    },
    { skip: !queryRegions.length }
  );
  const [
    getInstanceUrl,
    {
      data: urlData,
      isLoading: isUrlLoading,
      isFetching: isUrlFetching,
      isError: isUrlError,
      error,
      isSuccess: isUrlSuccess
    }
  ] = useLazyGetHaInstanceAccessUrlQuery();
  const {
    data: vmStatus,
    isSuccess: isVmStatusSuccess,
    isLoading: isVmStatusLoading
  } = useGetVmLiveStatusQuery(
    formatQueryRes({
      account: account,
      instance: instance,
      region: region
    }),
    { skip: !region?.token || !hasInstance }
  );

  const {
    data: instanceInfo,
    isError: instanceInfoError,
    isSuccess: instanceInfoSuccess,
    refetch: refetchInfo
  } = useGetInstanceInfoQuery(
    formatQueryRes({
      account: account,
      instance_id: instance.instance_id,
      region
    }),
    { skip: !region.token || !hasInstance }
  );

  const { data: instanceHistoric } = useGetHistoricQuotaQuery(
    formatQueryRes({
      account: account,
      instance_id: instance.instance_id,
      region
    }),
    { skip: !region?.token || !hasInstance }
  );
  // need to fetch instance list at first time event no region in account
  // so can trigger the invalidateTags when first provision
  useEffect(() => {
    // group user will not query user permission, so only check user_fullaccess for group account
    setIsAccountFullAccess(
      IsFullAccess(account, HomeElms.fcName) || account?.user_fullaccess
    );

    if (account?.has_instance || account?.instid) {
      setHasInstance(true);
    } else {
      setHasInstance(false);
    }
  }, [account]);
  useEffect(() => {
    const regionArr = Object.values(regions)?.filter(
      (obj: Record<string, any>) => obj?.token
    );
    if (regionArr?.length) {
      setQueryRegions(regionArr);
    }
  }, [regions]);

  useEffect(() => {
    if (instList?.length) {
      // currently one instance per region
      const validRegionList = instList.filter(
        (res: Record<string, any>) => res.data?.length
      );
      const res = validRegionList?.[0];
      if (res) {
        const instanceData = res?.data?.[0] || {};
        setInstance({
          ...instanceData,
          region_name: res?.region?.name
        });
        setRegion({ ...res?.region });
      } else {
        setInstance({});
        setRegion({});
      }
    }
  }, [instList]);

  useEffect(() => {
    if (instance?.incomplete_products || instance?.incomplete) {
      setDisableEnter(true);
      const text = instance?.incomplete_products?.length
        ? `Instance Provisioning Incomplete: ${instance?.incomplete_products?.join(', ')}`
        : 'Instance Provisioning Incomplete';
      setDisableEnterReason(text);
    }
  }, [instance]);

  useEffect(() => {
    if (!isAccountFullAccess) {
      setDisableWebssh(true);
      setDisableWebsshReason('Account is not full access');
    } else if (instance?.state_status === 'Upgrading') {
      setDisableWebssh(true);
      setDisableWebsshReason('Instance is Upgrading');
    } else {
      setDisableWebssh(false);
      setDisableWebsshReason('');
    }
  }, [instance?.state_status, isAccountFullAccess]);

  useEffect(() => {
    const warningArr = [];
    if (instanceInfoError) {
      warningArr.push({
        type: 'warning',
        message: `FortiSOC Service Unavailable. Please contact the support team for assistance.`
      });
    } else if (instanceInfoSuccess) {
      if (instanceInfo.tickets?.length) {
        setHasTicket(true);
        setTickets(instanceInfo.tickets);
      }
      setAnalyticsStr(
        daysToMonths(instanceInfo?.entitled_analytics_retention) + ' Month(s)'
      );
      setArchiveStr(
        daysToMonths(instanceInfo?.entitled_archive_retention) + ' Month(s)'
      );

      const entitled = Number(instanceInfo?.entitled_daily_ingestion || 0);
      const used =
        Number(instanceHistoric?.quota_used_last_24_hours || 0) / 1000;
      const usedStr = used.toFixed(2);
      const percent = entitled > 0 ? Math.ceil((used / entitled) * 100) : 0;
      const str = `${usedStr} GB / ${entitled} GB quota`;
      const detail = `Avg. daily usage (previous 6 days): ${usedStr} GB/Day - Entitled quota: ${entitled} GB/Day`;
      setDailyData({ percent, str, detail });
      if (instanceInfo?.is_expired) {
        warningArr.push({
          type: 'error',
          message: `${t('common.carouselMessageDialog.expiredMessage')}`
        });
      }
      if (used > entitled) {
        warningArr.push({
          type: 'warning',
          message: `The daily ingestion usage is exceeding the entitled limit.`
        });
      }
    }
    dispatch(updateWarnings([...warningArr]));
  }, [instanceInfo, instanceInfoError, instanceInfoSuccess, instanceHistoric]);

  useEffect(() => {
    if (instanceInfoSuccess && vmStatus) {
      const isDisableAccessInUpgradingVm =
        systemData?.disable_access_in_upgrading_vm ?? true;
      if (instanceInfo?.scheduled_upgrade_time) {
        setInstance((prev) => ({
          ...prev,
          state_status: `Upgrade Scheduled at ${getDateStrFromUnixTimestamp(
            instanceInfo.scheduled_upgrade_time
          )}`
        }));
      } else if (instanceInfo?.state === 'UPGRADING') {
        setInstance((prev) => ({
          ...prev,
          state_status: 'Upgrading'
        }));
        if (isDisableAccessInUpgradingVm) {
          setDisableEnter(true);
          setDisableEnterReason('Instance is Upgrading');
        }
      } else {
        setInstance((prev) => ({
          ...prev,
          state_status:
            instanceInfo?.state ||
            `${vmStatus?.is_active ? 'Active' : 'Inactive'}`
        }));
      }
    }
  }, [
    instanceInfo,
    instanceInfoSuccess,
    isVmStatusSuccess,
    vmStatus,
    systemData
  ]);

  useEffect(() => {
    if (isUrlSuccess) {
      if (urlData.url) {
        redirectVmUrl(urlData, '_blank');
      } else {
        ErrorToaster({
          toaster,
          error,
          title: 'Could not connect to the instance!'
        });
      }
    }
    if (isUrlError) {
      ErrorToaster({
        toaster,
        error,
        title: 'Could not connect to the instance!'
      });
    }
  }, [urlData, isUrlError]);

  const goToWebssh = () => {
    if (window) {
      // remove previous sid if exists
      const prevSid = sessionStorage.getItem('lastSid');
      if (prevSid) {
        sessionStorage.removeItem(prevSid);
      }
      const data = {
        account_id: account?.account_id || -1,
        user_id: account?.user_id,
        access_from_user_id:
          account.access_from_user_id || account?.access_from?.user_id,
        region_url: region?.url || '',
        token: region?.token || '',
        instance_id: instance?.instance_id || '',
        group_id: account?.group_id || ''
      };
      const dataString = encodeURIComponent(JSON.stringify(data));
      const id = crypto.randomUUID();
      let url = `/webssh/?sid=${id}`;
      sessionStorage.setItem(id, dataString);
      sessionStorage.setItem('lastSid', id);
      if (debugKey) {
        url += `&debug_key=${debugKey}`;
      }
      window.open(url, '_blank');
    }
  };
  return hasTicket ? (
    <TicketContent data={tickets} />
  ) : (
    <>
      {!instance?.is_adom ? (
        <>
          <Stack
            spacing={'30px'}
            className="instance-container"
            alignItems="flex-start"
          >
            <Stack.Item>
              <img src={InstanceIcon} className="service-icon" />
            </Stack.Item>
            <Stack.Item flex={1} className="info-content">
              <div className="service-title">{' Instance Information '}</div>
              <Stack className="info-row">
                <Stack.Item className="info-label">
                  {'Serial Number'}
                </Stack.Item>
                <Stack.Item>{instance?.license_sn}</Stack.Item>
              </Stack>
              <Stack className="info-row">
                <Stack.Item className="info-label">
                  {'Expiration Date'}
                </Stack.Item>
                <Stack.Item>{instance?.expiry_date}</Stack.Item>
              </Stack>
              <Stack className="info-row">
                <Stack.Item className="info-label">{'Region'}</Stack.Item>
                <Stack.Item>{instance?.region_name}</Stack.Item>
              </Stack>
              <Stack className="info-row">
                <Stack.Item className="info-label">{'Status'}</Stack.Item>
                <Stack.Item>{instance?.state_status}</Stack.Item>
              </Stack>
              <Stack className="info-row" alignItems="flex-start">
                <Stack.Item className="info-label">{'Entitlements'}</Stack.Item>
                <Stack.Item>
                  <Stack className="info-row" alignItems="center">
                    <Stack.Item className="info-sub-label">
                      {'Daily Ingestion'}
                    </Stack.Item>
                    <Stack.Item>
                      <Progress.Line
                        percent={dailyData?.percent}
                        style={{ width: '80px' }}
                        showInfo={false}
                        status={dailyData?.percent > 100 ? 'fail' : undefined}
                      />
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={
                          <Popover
                            title=""
                            className="instance-tooltip-popover"
                          >
                            <div className="instance-tooltip-content">
                              {dailyData?.detail}
                            </div>
                          </Popover>
                        }
                        enterable
                      >
                        <div className="progress-info">{dailyData?.str}</div>
                      </Whisper>
                    </Stack.Item>
                    <Stack.Item>
                      <InstanceHistoric
                        account={account}
                        instance={instance}
                        region={region}
                      />
                    </Stack.Item>
                  </Stack>
                  <Stack className="info-row">
                    <Stack.Item className="info-sub-label">
                      {'Analytics Retention'}
                    </Stack.Item>
                    <Stack.Item>{analyticsStr}</Stack.Item>
                  </Stack>
                  <Stack className="info-row">
                    <Stack.Item className="info-sub-label">
                      {'Archive Retention'}
                    </Stack.Item>
                    <Stack.Item>{archiveStr}</Stack.Item>
                  </Stack>
                </Stack.Item>
                {/* <Stack.Item>{instance?.license_sn}</Stack.Item> */}
              </Stack>
            </Stack.Item>
          </Stack>
        </>
      ) : (
        <></>
      )}
      <ButtonToolbar
        style={{
          width: '360px',
          marginLeft: '78px',
          marginTop: '20px',
          marginBottom: '60px'
        }}
      >
        <div className="enter-button-wrapper">
          <Button
            appearance="primary"
            disabled={disableEnter}
            className="action-btn"
            startIcon={<MdArrowForward />}
            loading={
              isVmStatusLoading ||
              isUrlFetching ||
              isUrlLoading ||
              region?.token === undefined
            }
            onClick={() => {
              getInstanceUrl({
                account: account,
                instance: instance,
                region: region
              });
            }}
          >
            Enter
          </Button>
          {disableEnter && (
            <Whisper
              placement="top"
              trigger="hover"
              speaker={
                <Popover title="" className="instance-tooltip-popover">
                  <div className="instance-tooltip-content">
                    {disableEnterReason}
                  </div>
                </Popover>
              }
            >
              <span
                className="enter-button-info"
                aria-label="Enter unavailable"
              >
                <MdInfoOutline />
              </span>
            </Whisper>
          )}
        </div>
        {(globalData?.enable_webssh || account?.group_id) &&
          !instance?.is_adom && (
            <div className="webssh-button-wrapper">
              <Button
                startIcon={<VscTerminalCmd />}
                appearance="ghost"
                disabled={disableWebssh}
                size="xs"
                onClick={() => goToWebssh()}
              >
                WebSSH
              </Button>
              {disableWebssh && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={
                    <Popover title="" className="instance-tooltip-popover">
                      <div className="instance-tooltip-content">
                        {disableWebsshReason}
                      </div>
                    </Popover>
                  }
                >
                  <span
                    className="enter-button-info"
                    aria-label="WebSSH unavailable"
                  >
                    <MdInfoOutline />
                  </span>
                </Whisper>
              )}
            </div>
          )}
      </ButtonToolbar>
    </>
  );
};
